import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_VW = 0.5;
const STORAGE_KEY = 'atlas.fixedRoutesDrawerWidthPx';
const LEGACY_STORAGE_KEY = 'atlas.fixedRoutesDrawerHeightPx';
/** Fraction of viewport for minimum drawer width */
const MIN_VW = 0.24;
/** Fraction of viewport for maximum drawer width */
const MAX_VW = 0.92;

function clampBounds(viewportWidth: number): { minPx: number; maxPx: number } {
  let maxPx = Math.round(viewportWidth * MAX_VW);
  let minPx = Math.round(viewportWidth * MIN_VW);
  if (minPx >= maxPx) {
    minPx = Math.max(280, Math.floor(maxPx * 0.45));
  }
  return { minPx, maxPx };
}

/**
 * Right drawer width + drag-to-resize.
 * Width is in px; min/max follow viewport size and update on window resize.
 */
export function useFixedBottomDrawerResize() {
  const [viewportW, setViewportW] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1400
  );

  useEffect(() => {
    const onResize = () => setViewportW(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const { minPx, maxPx } = clampBounds(viewportW);

  const [widthPx, setWidthPx] = useState(() => {
    if (typeof window === 'undefined') return 560;
    const { minPx: mn, maxPx: mx } = clampBounds(window.innerWidth);
    const persistedRaw = window.localStorage.getItem(STORAGE_KEY);
    const legacyPersistedRaw = window.localStorage.getItem(LEGACY_STORAGE_KEY);
    const persisted = persistedRaw
      ? Number(persistedRaw)
      : legacyPersistedRaw
        ? Number(legacyPersistedRaw)
        : NaN;
    const target = Number.isFinite(persisted)
      ? Math.round(persisted)
      : Math.round(window.innerWidth * DEFAULT_VW);
    return Math.max(mn, Math.min(mx, target));
  });

  // Keep width within bounds when viewport or limits change
  useEffect(() => {
    setWidthPx((prev) => Math.max(minPx, Math.min(maxPx, prev)));
  }, [minPx, maxPx]);

  const [isResizing, setIsResizing] = useState(false);
  const [isTouchResizing, setIsTouchResizing] = useState(false);
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(widthPx);

  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsResizing(true);
      resizeStartX.current = e.clientX;
      resizeStartWidth.current = widthPx;
    },
    [widthPx]
  );

  const handleResizeTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length !== 1) return;
      e.preventDefault();
      setIsTouchResizing(true);
      resizeStartX.current = e.touches[0].clientX;
      resizeStartWidth.current = widthPx;
    },
    [widthPx]
  );

  const nudgeWidth = useCallback(
    (deltaPx: number) => {
      setWidthPx((prev) => Math.max(minPx, Math.min(maxPx, prev + deltaPx)));
    },
    [minPx, maxPx]
  );

  useEffect(() => {
    if (!isResizing) return;
    const onMove = (e: MouseEvent) => {
      const delta = resizeStartX.current - e.clientX;
      const next = Math.max(minPx, Math.min(maxPx, resizeStartWidth.current + delta));
      setWidthPx(next);
    };
    const onUp = () => setIsResizing(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isResizing, minPx, maxPx]);

  useEffect(() => {
    if (!isTouchResizing) return;
    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      const delta = resizeStartX.current - e.touches[0].clientX;
      const next = Math.max(minPx, Math.min(maxPx, resizeStartWidth.current + delta));
      setWidthPx(next);
    };
    const onTouchEnd = () => setIsTouchResizing(false);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd);
    window.addEventListener('touchcancel', onTouchEnd);
    return () => {
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [isTouchResizing, minPx, maxPx]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEY, String(widthPx));
  }, [widthPx]);

  return {
    widthPx,
    isResizing: isResizing || isTouchResizing,
    handleResizeMouseDown,
    handleResizeTouchStart,
    nudgeWidth,
  };
}

export const FIXED_ROUTES_RESIZE_HANDLE_WIDTH = 6;
